import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import LOCALES from "./locales.json";

// Supported languages (kept alongside the toggle order in <LanguageSwitcher />)
export const SUPPORTED_LANGS = ["en", "fr", "ht"];
export const LANG_LABEL = { en: "English", fr: "Français", ht: "Kreyòl" };

const OVERRIDE_KEY = "seayisyen.i18n.overrides";

function loadOverrides() {
  try { return JSON.parse(localStorage.getItem(OVERRIDE_KEY) || "{}"); }
  catch { return {}; }
}
function persistOverrides(o) {
  try { localStorage.setItem(OVERRIDE_KEY, JSON.stringify(o)); } catch { /* noop */ }
}

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState(() => {
    const stored = localStorage.getItem("seayisyen.lang");
    return SUPPORTED_LANGS.includes(stored) ? stored : "en";
  });
  // Per-language dot-path overrides so corrections can ship without a redeploy.
  const [overrides, setOverrides] = useState(loadOverrides);

  useEffect(() => {
    localStorage.setItem("seayisyen.lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback((path, fallback = "") => {
    // 1) override layer (lang first, then EN fallback)
    const ov = overrides[lang]?.[path] ?? overrides.en?.[path];
    if (ov !== undefined) return ov;
    // 2) shipped dictionary
    const dict = LOCALES[lang] || LOCALES.en;
    const val = path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), dict);
    if (val !== undefined) return val;
    // 3) EN fallback if missing in selected lang
    if (lang !== "en") {
      const enVal = path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), LOCALES.en);
      if (enVal !== undefined) return enVal;
    }
    return fallback ?? path;
  }, [lang, overrides]);

  // Programmatic single-key correction — global, instant, persisted.
  const setTranslation = useCallback((targetLang, dotPath, value) => {
    setOverrides((prev) => {
      const next = { ...prev, [targetLang]: { ...(prev[targetLang] || {}), [dotPath]: value } };
      persistOverrides(next);
      return next;
    });
  }, []);

  // Bulk patch — accepts `{ ht: { "hero.register": "Enskri", ... }, fr: {...} }`
  const setTranslations = useCallback((patch) => {
    setOverrides((prev) => {
      const next = { ...prev };
      Object.entries(patch || {}).forEach(([lng, kv]) => {
        next[lng] = { ...(next[lng] || {}), ...(kv || {}) };
      });
      persistOverrides(next);
      return next;
    });
  }, []);

  const resetOverrides = useCallback((targetLang) => {
    setOverrides((prev) => {
      const next = targetLang ? { ...prev, [targetLang]: {} } : {};
      persistOverrides(next);
      return next;
    });
  }, []);

  // Expose a tiny global so corrections can be tested from the browser console
  // without rebuilding the app:
  //   window.seayisyenI18n.set('ht', 'hero.register', 'Enskri');
  //   window.seayisyenI18n.bulk({ ht: { 'nav.login': 'Konekte' } });
  //   window.seayisyenI18n.reset('ht');
  useEffect(() => {
    window.seayisyenI18n = {
      get current() { return { lang, overrides, locales: LOCALES }; },
      set: setTranslation,
      bulk: setTranslations,
      reset: resetOverrides,
      switch: setLang,
    };
  }, [lang, overrides, setTranslation, setTranslations, resetOverrides]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, setTranslation, setTranslations, resetOverrides, overrides, locales: LOCALES }}>
      {children}
    </I18nContext.Provider>
  );
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be inside I18nProvider");
  return ctx;
};
